/**
 * X75V2 机甲：未改键 `getKeyboardKeys` 样例（layer0）→ 布局 HID / 分位值 → `pos`。
 * 与 `KEYBOARD_LAYOUTS.x75v2` 虚拟键盘回显、改键/灯光 pos 解析一致。
 */
const CB_VALUE_TO_HID: Readonly<Record<number, number>> = {
  65536: 224,
  131072: 225,
  262144: 226,
  524288: 227,
  1048576: 228,
  2097152: 229,
  4194304: 230,
  218103808: 61697,
};

/** 设备上报的 [value, pos]；来自 X75V2 实机 getKeyboardKeys({ layer: 0 }) */
const X75V2_INIT_VALUE_POS: ReadonlyArray<readonly [number, number]> = [
  [41, 8],
  [53, 12],
  [43, 16],
  [57, 20],
  [131072, 24],
  [65536, 28],
  [30, 36],
  [20, 40],
  [4, 44],
  [29, 48],
  [524288, 52],
  [58, 56],
  [31, 60],
  [26, 64],
  [22, 68],
  [27, 72],
  [262144, 76],
  [59, 80],
  [32, 84],
  [8, 88],
  [7, 92],
  [6, 96],
  [60, 104],
  [33, 108],
  [21, 112],
  [9, 116],
  [25, 120],
  [61, 128],
  [34, 132],
  [23, 136],
  [10, 140],
  [5, 144],
  [44, 148],
  [62, 152],
  [35, 156],
  [28, 160],
  [11, 164],
  [17, 168],
  [63, 176],
  [36, 180],
  [24, 184],
  [13, 188],
  [16, 192],
  [64, 200],
  [37, 204],
  [12, 208],
  [14, 212],
  [54, 216],
  [218103808, 220],
  [65, 224],
  [38, 228],
  [18, 232],
  [15, 236],
  [55, 240],
  [1048576, 244],
  [66, 248],
  [39, 252],
  [19, 256],
  [51, 260],
  [56, 264],
  [67, 272],
  [45, 276],
  [47, 280],
  [52, 284],
  [68, 296],
  [46, 300],
  [48, 304],
  [50, 308],
  [100, 312],
  [69, 320],
  [42, 324],
  [49, 328],
  [40, 332],
  [2097152, 336],
  [80, 340],
  [73, 344],
  [82, 360],
  [81, 364],
  [117440541, 368],
  [76, 372],
  [75, 376],
  [78, 380],
  [79, 388],
];

/** X75V2 旋钮出厂 value（与 UI 码 8418 对应） */
export const X75V2_KNOB_DEVICE_VALUE = 117440541 as const;

function buildX75v2FactoryPosByKeycode(): Readonly<Record<number, number>> {
  const o: Record<number, number> = {};
  for (const [value, pos] of X75V2_INIT_VALUE_POS) {
    const hid = CB_VALUE_TO_HID[value] ?? value;
    o[hid] = pos;
  }
  /** 与 `KEYBOARD_LAYOUTS.x75v2` 旋钮虚拟码一致 */
  o[8418] = 368;
  return o;
}

export const X75V2_FACTORY_POS_BY_KEYCODE: Readonly<Record<number, number>> =
  buildX75v2FactoryPosByKeycode();

/**
 * SDK Device916：`pos = 8 + t*4`，`light_pos = 8 + t`。
 * 故 light_pos = 8 + (pos - 8) / 4；INIT 表有空洞，绝不能用数组下标当 t。
 */
export function x75v2LightPosFromFactoryPos(pos: number): number {
  return 8 + (Number(pos) - 8) / 4;
}

/**
 * X75V2 layer0 出厂键表（供 setGifLight 律动稠密平面写入）。
 */
export function buildX75v2FactoryKeyboardKeysForGifLight(
  maxSlots = 126
): Array<{ value: number; pos: number; light_pos: number }> {
  return X75V2_INIT_VALUE_POS.slice(0, maxSlots).map(([value, pos]) => ({
    value,
    pos,
    light_pos: x75v2LightPosFromFactoryPos(pos),
  }));
}
